import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  NgZone,
  signal,
  inject,
  input,
  PLATFORM_ID,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Viewport, ISnapZoomOptions } from 'pixi-viewport';
import { Application, Assets, Container, Graphics, Point, Sprite, Texture, Text } from 'pixi.js';
import { onlyInLeft } from 'PreciateUtil';
import { combineLatest, fromEvent, ReplaySubject } from 'rxjs';
import { filter, pairwise, startWith, take, takeUntil } from 'rxjs/operators';

import { AppComponent } from 'app/app.component';
import { LaunchDarklyFlags, PixiInspector } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { formatUserName } from 'app/core/utilities/user-util';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import { notUndefined } from 'app/utils/stream-util';
import CTAItem from 'entity/CTAItem';
import SocialArtifact from 'entity/SocialArtifact';
import { MINIMAP_MODE } from 'entity/SocialSettings';
import SocialTheme from 'entity/SocialTheme';
import User from 'entity/User';
import {
  IncomingMessageType,
  IncomingMessage,
  MovedMessage,
  ParticipantMovement,
  ResizeMessage,
  RingColor,
} from 'messages/websocket.messages';

import { COLOR_WHITE, PLAYER_RADIUS } from '../constants';
import { createArtifactSprite } from '../services/artifact/artifact.helper';
import { BadgingService } from '../services/badging/badging.service';
import { createCTAImage } from '../services/cta/cta.helper';
import { SceneService } from '../services/scene.service';
import { TeleportService } from '../services/teleport.service';
import { WebsocketService } from '../services/websocket.service';
import { ZIndex } from '../social-pixi-canvas/constants';
import { generateMapButtonY } from '../social-pixi-canvas/cta/cta-item';
import MouseMovementController from '../social-pixi-canvas/mouse-movement-controller';
import { Player } from '../social-pixi-canvas/player';
import { selectors, socialAppActions, socialAppSelectors } from '../state/app';
import { RoomSizeType } from '../types/room';

import MiniMapHelper from './mini-map-helper';
import MiniMapParticipant from './mini-map-participant';
import { MiniMapState } from './mini-map-state';
import '@pixi/gif';
import { initDevtools } from '@pixi/devtools';
import { BrowserService } from '../services/browser.service';
import { PORTRAIT_MAP_HORIZONTAL_PADDING, PORTRAIT_MAP_VERTICAL_PADDING } from './mini-map-helper.constants';

/*

Base rules for Mini Map

1. Mini Map size behaves based on state
  a. CLOSED -> map is not visible
  b. OPENED -> map is displays a section of the entire mini map
  c. EXPANDED -> map displays entire world
  d. MOBILE -> responsive map that replicates the behavior of the expanded state

2. Mini Map Layers in parent child relation
 Mini Map Component -> entire component with map toggle button
 --- Mini Map Stage HTML Element View (Pixi Container) -> Main view where the mini map renders (this is the section you always see)
     ---- viewport -> contains the entire world
          ---- world layer -> graphic that encapsulates the entire world and it's limits (you only see all of it on EXPANDED and zoomed out all the way)
          ---- participants -> bubbles that represent the participants (scale based on big world size)
          ---- navigation artifacts -> compass items for navigation

3. Sizing & Scaling behavior
   a. mini map world is always a 400 x 400 size
   b. Ratio and scale is calculates based on the current size of the big world
      mini map world size / big world size -> (400 / big world size)
   c. sizing of mini map stage is based on state
      i. OPENED: 300 x 300 px
      ii. EXPANDED: 400 x 400 px
      iii. MOBILE: handle portrait and landscape view in mini-map-helper

4. Zoom
  a. Zoom is only available in EXPANDED state
  b. Zoom in / out is by 10% in relation to the mini map world size (0.1 * 400 = 40 -> so you zoom by 40px)
  c. Max Zoom Out is to 400px
  d. Max Zoom In is to 160px
  e. Zoom In centers the viewport to the main participant
  f. Zoom Out centers the viewport to main participant
     unless max zoom out is reached then the viewport centers
     to the center of the world.
  g. Zoom remains the same for every state of the mini map.

5. Drag
  a. Viewport can only be dragged under EXPANDED and MOBILE state and when you are zoomed in.
  b. Drag gets disabled under OPENED state or when max zoom out is reached

6. Center
  a. Center is only available under EXPANDED and MOBILE state
  b. Center re-centers the viewport to the main participant when you are zoomed in and not in view

7. Viewport Camera Movement
  a. Viewport Camera does not move in EXPANDED or MOBILE state when zoomed out
  b. When zoomed in or in OPENED state the camera moves to the center of the main participant
  c. if viewport camera is dragged then user must center or zoom out all the way to re-center the viewport camera


*/
const MAP_BORDER = 3;
const BORDER_COLOR = 0xffffff;
const DEFAULT_ZOOM = 400;
const ZOOM_SNAP_TIME = 300;
const DEFAULT_ROOM_SIZE = 4000;
const DEFAULT_RADIUS = 11;
const DEFAULT_RATIO = 0.1;
const MAP_HOVER_PADDING = 10;
const MIN_MOVEMNT_DISTANCE = 5;
const MINIMAP_LAST_STATE_KEY = 'minimap_last_state';
@Component({
  selector: 'app-social-mini-map',
  templateUrl: './social-mini-map.component.html',
  styleUrls: ['./social-mini-map.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SocialMiniMapComponent extends Loggable implements OnDestroy, OnInit {
  private elementRef = inject(ElementRef);
  private sceneService = inject(SceneService);
  private websocketService = inject(WebsocketService);
  private store = inject(Store);
  private badgingService = inject(BadgingService);
  private teleportService = inject(TeleportService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private browserService = inject(BrowserService);
  private ngZone = inject(NgZone);
  private platformId = inject(PLATFORM_ID);

  public readonly user = input<User>();

  private ktx2Compression = false;

  /* Mini Map */
  protected app?: Application;
  public stage?: Container;
  public element?: HTMLElement;
  public mapGraphic?: Graphics;
  public worldLayer?: Graphics;
  public backgroundLayer?: Graphics;
  public viewport?: Viewport;

  /* Helpers */
  public miniMapHelper: MiniMapHelper;
  private teleportMenuTimeout: number | undefined;
  private teleportContainer!: Container;
  private teleportTooltip: Text | undefined;
  private teleportTooltipTimeout!: number;
  private teleportTooltipRectangleBackground: Graphics | undefined;
  private readonly isMapTeleport = signal(false);

  public get state(): typeof MiniMapState {
    return MiniMapState;
  }

  public get mapHeight(): number {
    return this.roomSize / 10;
  }

  public get mapWidth(): number {
    return this.roomSize / 10;
  }

  public get mapScale(): number {
    return this.roomSize / 10;
  }

  private compressed(uri: string) {
    return this.ktx2Compression ? uri?.replace('webp', 'ktx2') : uri;
  }

  /* Mini Map Items */
  public participants = new Map<string, MiniMapParticipant>();

  public hoverUser?: User;
  protected hoverUserNameFromBadge?: string;
  public dragging = false;
  public draggingEnabled = false;
  public isCurrentlyDragging = false;
  public isMobileResponsiveWidth = this.browserService.isMobileBreakpoint(); //responsive view for mobile devices
  public isMobile = this.browserService.isMobile();
  public isPortraitMode = window.innerHeight > window.innerWidth;
  public isResponsiveSnapZoom = false;
  public radius = 0;
  public guestCount = 1;

  private destroyed$ = new ReplaySubject<boolean>(1);
  private artifactSprites = new Map<string, Sprite>();
  private artifacts: SocialArtifact[] = [];
  private movements: ParticipantMovement[] = [];
  private ctaSprites = new Map<string, Container>();
  private ctaItems: CTAItem[] = [];
  private navItemSprites = new Map<string, Sprite>();
  private ringColorsList: RingColor[] = [];
  protected hoverPlayer?: Player;

  /* Selected room for dropdown */
  public selectedRoomId?: string;

  /* Guest API */
  private setGuestMiniMap = false;
  private guestMiniMapState?: MiniMapState;

  // Default map mode
  private readonly defaultMode = signal(MiniMapState.OPENED);
  private checkIsMapInitiallyClosed = false;

  private theme?: SocialTheme;
  private roomSize = DEFAULT_ROOM_SIZE;

  // Find participant feature
  private openedByFindFeature = false;
  private foundParticipant?: MiniMapParticipant;

  //Presentation Check
  private presentationActive = false;

  protected stageHeight?: string;
  protected stageWidth?: string;
  protected readonly newVisualIndicators = signal(false);
  public readonly isMouseOver = signal<boolean>(false);
  private draggingState = false;

  // --- Top Left Corner Detection ---
  public topDetectionThreshold = 70; // Configurable corner size (px)
  public readonly isMouseInTopLeftArea = signal(false);

  private lastPosition = { x: 0, y: 0 };

  public constructor() {
    super();
    this.setDisplayName('SocialMiniMapComponent');
    this.miniMapHelper = new MiniMapHelper();

    // Room Settings
    this.store
      .select(socialAppSelectors.getRoomSettings)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        const mode = this.sceneService.getSetting(MINIMAP_MODE);
        if (typeof mode !== 'number') {
          this.defaultMode.set(MiniMapState.OPENED);
          return;
        }
        this.defaultMode.set(mode);
        this.changeDetectorRef.markForCheck();
      });

    // Customer API Mini Map Settings
    this.store
      .select(socialAppSelectors.isGuestMiniMapStateApplied)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((state) => {
        this.setGuestMiniMap = state;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.getGuestMiniMapState)
      .pipe(filter(notUndefined), takeUntil(this.destroyed$))
      .subscribe((setting) => {
        this.guestMiniMapState = setting;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(selectors.getRoomSize)
      .pipe(filter(notUndefined), takeUntil(this.destroyed$))
      .subscribe((roomSize) => {
        this.roomSize = roomSize ?? DEFAULT_ROOM_SIZE;
        this.changeDetectorRef.markForCheck();
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.ktx2_compression)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((ktx2Compression) => {
        this.ktx2Compression = ktx2Compression;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(selectors.getTheme)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((theme) => {
        this.theme = theme;
        this.changeDetectorRef.markForCheck();
      });

    SocialMiniMapComponent.instance = this;

    this.store
      .select(socialAppSelectors.getCurrentSocial)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((social) => {
        this.selectedRoomId = social?.socialId;
        this.changeDetectorRef.markForCheck();
      });

    // Switch the theme background when the user changes rooms
    this.store
      .select(socialAppSelectors.getCurrentSocial)
      .pipe(
        pairwise(),
        filter(
          ([previousSocial, social]) =>
            notNil(previousSocial) && notNil(social) && previousSocial.themeId !== social.themeId,
        ),
        takeUntil(this.destroyed$),
      )
      .subscribe(([, social]) => {
        if (social) {
          const size = social.startingWidth ?? DEFAULT_ROOM_SIZE;
          this.runIfPixiInitialized(() => {
            this.handleRoomChange(size);
          });
        }
      });

    this.websocketService.message$.pipe(takeUntil(this.destroyed$)).subscribe(this.handleWebsocketMessage.bind(this));

    combineLatest([
      this.store.select(socialAppSelectors.isSearchOpen),
      this.store.select(socialAppSelectors.isReactionsOpen),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([isSearchParticipantOpen, isReactionsOpen]) => {
        if (isSearchParticipantOpen || isReactionsOpen) {
          this.setMapState(MiniMapState.CLOSED);
          if (this.openedByFindFeature) {
            this.resetFindParticipant();
          }
          this.changeDetectorRef.markForCheck();
        }
      });

    // Show expanded map after user tries to find other participant on badging card
    this.store
      .select(socialAppSelectors.shouldOpenMap)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((findParticipant) => {
        this.handleFindParticipant(findParticipant);
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.getVisualIndicatorInfo)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((visualIndicatorInfo) => {
        if (visualIndicatorInfo.colors) {
          this.ringColorsList = visualIndicatorInfo.colors;
          this.changeDetectorRef.markForCheck();
        }
      });

    this.store
      .select(socialAppSelectors.isPresentationActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationActive) => {
        this.presentationActive = isPresentationActive;
        if (isPresentationActive) {
          this.toggleMap(MiniMapState.CLOSED);
          this.changeDetectorRef.markForCheck();
        }
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.visual_indicators)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((newVisualIndicators) => this.newVisualIndicators.set(newVisualIndicators));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.map_teleport)
      .pipe(
        filter((enabled) => enabled),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => this.isMapTeleport.set(true));

    this.store
      .select(socialAppSelectors.toggleMapOnboardingExperience)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((toggleMapOnboardingExperience) => {
        if (toggleMapOnboardingExperience) {
          this.setMapState(this.defaultMode());
        } else {
          // viewport is not initialized yet, resize will fail
          this.setMapState(MiniMapState.CLOSED, false);
        }
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.showKnowledgeBaseChatbot)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((showKnowledgeBaseChatbot) => {
        if (showKnowledgeBaseChatbot) {
          this.setMapState(MiniMapState.CLOSED);
        }
        this.changeDetectorRef.markForCheck();
      });

    // only effect pixi state

    if (!this.sceneService.sceneManager) {
      this.error('Scene Manager is not defined');
      return;
    }
    // Handle current participants in room
    this.sceneService.sceneManager.participantAdded$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(this.addParticipant.bind(this));

    this.sceneService.sceneManager.participantRemoved$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(this.removeParticipant.bind(this));

    this.sceneService.sceneManager.app?.ticker.add(this.updatePlayers, this);

    fromEvent(window, 'resize')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.isMobileResponsiveWidth = this.browserService.isMobileBreakpoint();
        this.isPortraitMode = window.innerHeight > window.innerWidth;
        this.setMapState(MiniMapState.CLOSED);
        // TODO: reset is breaking the map, do we need this?
        // this.resetMap(true);
        this.changeDetectorRef.markForCheck();
      });

    this.isMobile = this.browserService.isMobile();
  }

  public static getInstance(): SocialMiniMapComponent {
    return this.instance;
  }

  public setVisibilityToDefault(): void {
    if (!this.presentationActive) {
      // Try to restore the last open state, otherwise use the default mode
      const lastState = this.getLastOpenState();
      let targetState: MiniMapState;

      if (lastState) {
        // Use the last saved state, but adjust for mobile if needed
        if (this.isMobileResponsiveWidth && lastState !== MiniMapState.MOBILE) {
          targetState = MiniMapState.MOBILE;
        } else if (!this.isMobileResponsiveWidth && lastState === MiniMapState.MOBILE) {
          targetState = this.defaultMode();
        } else {
          targetState = lastState;
        }
      } else {
        targetState = this.defaultMode();
      }

      this.setMapState(targetState);
    } else {
      this.setMapState(MiniMapState.CLOSED);
    }
    this.changeDetectorRef.markForCheck();
  }

  public ngOnInit() {
    const initialState = this.getMiniMapDefaultState();

    this.info(`Mini Map Initial State: ${initialState}`);
    this.miniMapHelper = new MiniMapHelper(initialState, this.mapWidth, this.mapHeight, this.mapScale);

    // if the user has updated their name be sure to update the name tag
    if (this.badgingService) {
      this.badgingService.badgeNames$.pipe(takeUntil(this.destroyed$)).subscribe((badgeNames) => {
        // find players and update names
        for (const badgeName in badgeNames) {
          const player = this.sceneService.sceneManager?.getParticipant(badgeName);
          if (player && badgeNames[badgeName]) {
            player.name = badgeNames[badgeName];
          }
        }
        this.changeDetectorRef.markForCheck();
      });
    }

    const width = this.miniMapHelper.width;
    const height = this.miniMapHelper.height;
    this.element = this.elementRef.nativeElement.querySelector('#stage');
    if (this.element) {
      this.element.style.width = width + 'px';
      this.element.style.height = height + 'px';
      this.element.style.overflow = 'hidden';
      this.element.style.borderRadius = '15px';

      if (this.isMobile) {
        this.element.style.left = '50%';
        this.element.style.transform = 'translateX(-50%)';
      }
    }

    void this.ngZone.runOutsideAngular(() => {
      this.app = new Application({
        resizeTo: this.element,
        backgroundAlpha: 0,
        resolution: 1,
        antialias: false,
        autoDensity: true,
      });

      getFeatureFlagObservable(this.store, LaunchDarklyFlags.pixi_inspector)
        .pipe(
          take(1),
          filter((pixi_inspector) => pixi_inspector === PixiInspector.MAP),
        )
        .subscribe(() => {
          void initDevtools({
            app: this.app,
          });
        });

      // TODO: remove when jest is fixed

      if (this.app && this.element) {
        this.element.appendChild(this.app.view as HTMLCanvasElement);
      }

      this.stage = new Container();
      this.stage.name = 'stage';
      this.stage.eventMode = 'static';
      this.stage.sortableChildren = true;
      this.stage.interactiveChildren = true;
      this.stage.visible =
        this.miniMapHelper.state === MiniMapState.OPENED || this.miniMapHelper.state === MiniMapState.EXPANDED;

      this.initMap();

      // Sets the current theme for the map
      if (this.theme) {
        this.updateTheme(this.theme);
      }
    });

    this.store
      .select(selectors.getArtifacts)
      .pipe(startWith([]), pairwise(), takeUntil(this.destroyed$))
      .subscribe(([prevArtifacts, artifacts]) => this.handleArtifactsChange(prevArtifacts, artifacts));

    this.store
      .select(selectors.getCTAItems)
      .pipe(startWith([]), pairwise(), takeUntil(this.destroyed$))
      .subscribe(([prevItems, items]) => this.handleCTAItemsChange(prevItems, items));
  }

  private getMiniMapDefaultState(): MiniMapState {
    if (this.setGuestMiniMap && this.guestMiniMapState) {
      return this.guestMiniMapState;
    }

    if (this.isMobileResponsiveWidth) {
      return MiniMapState.CLOSED;
    }

    return this.defaultMode();
  }

  public runIfPixiInitialized(callback: () => void): void {
    if (this.app?.stage) {
      callback();
    } else {
      this.app?.ticker.addOnce(() => {
        callback();
      });
    }
  }

  // Update the map scale and properties when switching to a connected room
  private handleRoomChange(size: number): void {
    this.roomSize = size;

    if (this.isMobile) {
      if (this.stage) {
        this.stage.visible = false;
      }
      this.miniMapHelper = new MiniMapHelper(MiniMapState.CLOSED, this.mapWidth, this.mapHeight, this.mapScale);
    } else {
      this.miniMapHelper = new MiniMapHelper(MiniMapState.OPENED, this.mapWidth, this.mapHeight, this.mapScale);
    }

    const width = this.miniMapHelper.width;
    const height = this.miniMapHelper.height;
    this.element = this.elementRef.nativeElement.querySelector('#stage');

    if (this.element) {
      this.element.style.width = width + 'px';
      this.element.style.height = height + 'px';
      this.element.style.overflow = 'hidden';
      this.element.style.borderRadius = '15px';

      if (this.isMobile) {
        this.element.style.left = '50%';
        this.element.style.transform = 'translateX(-50%)';
      }
    }

    // Reset Ratio and Radius
    this.miniMapHelper.ratio = DEFAULT_RATIO;
    this.radius = DEFAULT_RADIUS;

    this.updateTheme(this.theme ?? {});
    this.stage?.removeChildren();
    this.viewport?.removeChildren();

    this.createMapContainer();

    void this.initViewport(true);
    this.initializeMapCenter();
    this.changeDetectorRef.markForCheck();
  }

  private updatePlayers(): void {
    this.movements.forEach(({ u: userId }) => {
      const player = this.sceneService.sceneManager?.getParticipant(userId);
      if (!player) {
        return;
      }
      const { x, y } = player;
      this.updatePlayerPosition({ userId, x, y });
    });
    if (this.stage && this.app) {
      this.app.stage.addChild(this.stage);
    }
  }

  public ngOnDestroy(): void {
    this.stage?.destroy({ children: true });
    this.store.dispatch(socialAppActions.TOGGLE_MAP_OPEN({ open: false, userId: '' }));
    this.participants.clear();
    // this.deleteNavItems();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  /**
   * Call this function when resetting values of the map
   */
  private resetMap(resetMapGraphic?: boolean): void {
    if (resetMapGraphic && this.mapGraphic) {
      this.stage?.removeChild(this.mapGraphic);
      this.mapGraphic.clear();

      this.createMapContainer();
    }

    if (this.viewport) {
      this.stage?.removeChild(this.viewport);
    }
    this.viewport?.destroy();

    if (this.worldLayer) {
      this.stage?.removeChild(this.worldLayer);
      this.worldLayer.clear();
      this.worldLayer = undefined;
    }

    if (this.backgroundLayer) {
      this.stage?.removeChild(this.backgroundLayer);
      this.backgroundLayer.clear();
      this.backgroundLayer = undefined;
    }
    void this.initViewport(true);
  }

  private updateTheme(theme: SocialTheme): void {
    if (!theme.colorPrimary) {
      return;
    }

    this.miniMapHelper.setBackgroundColor(theme.colorPrimary);
    this.participants.forEach((participant) => participant.setTheme(theme));
    this.resetMap(true);
  }

  private async createWorldLayer(): Promise<void> {
    if (!this.viewport) {
      return;
    }

    // Create background layer graphic
    if (this.theme?.addToMiniMap && this.theme?.minimapImageURI) {
      await Assets.load(this.compressed(this.theme.minimapImageURI));
      const imageTexture = Texture.from(this.compressed(this.theme.minimapImageURI) ?? '');
      this.backgroundLayer = new Graphics()
        .lineStyle(MAP_BORDER, BORDER_COLOR)
        .beginTextureFill({ texture: imageTexture })
        .drawRoundedRect(0, 0, this.miniMapHelper.worldWidth, this.miniMapHelper.worldHeight, 15)
        .endFill();
      this.backgroundLayer.name = 'background-layer';
      this.backgroundLayer.x = 3;
      this.backgroundLayer.y = 3;
    }

    const w = this.miniMapHelper.worldWidth + 5;
    const h = this.miniMapHelper.worldHeight + 5;

    // Create mini world layer graphic
    this.worldLayer = new Graphics()
      .beginFill(this.miniMapHelper.backgroundColor, 0.5)
      .lineStyle(MAP_BORDER, BORDER_COLOR)
      .drawRoundedRect(0, 0, w, h, 15)
      .endFill();
    this.worldLayer.name = 'world-layer';
    this.worldLayer.sortableChildren = true;
    this.worldLayer.zIndex = ZIndex.Border;

    this.miniMapHelper.worldWidth = this.mapWidth;
    this.miniMapHelper.worldHeight = this.mapHeight;

    // First make sure the viewport is added to the stage
    if (this.mapGraphic && this.stage) {
      // Add mapGraphic to stage if not already added
      if (!this.mapGraphic.parent) {
        this.stage.addChildAt(this.mapGraphic, 0);
      }

      // Add viewport to stage if not already added
      if (!this.viewport.parent) {
        this.stage.addChildAt(this.viewport, 1);
      }

      // Now add layers to the viewport
      if (this.backgroundLayer) {
        this.viewport.addChild(this.backgroundLayer);
      } else if (this.worldLayer) {
        this.viewport.addChild(this.worldLayer);
      }
    }

    void this.enableMapTeleport();
  }

  /* Mini Map Init */

  private initMap(): void {
    this.info('Initialize Map');
    if (!isPlatformBrowser(AppComponent.PLATFORM_ID)) {
      return;
    }
    // set map color and save to hex
    if (this.theme?.colorPrimary) {
      this.miniMapHelper.setBackgroundColor(this.theme.colorPrimary);
    }

    // Create Map Container Graphic
    this.createMapContainer();

    // Init Viewport
    void this.initViewport();
    this.stage?.scale.set(1, 1);
    this.miniMapHelper.ratio = this.getRatio({});

    // close for intro animation
    this.setMapState(MiniMapState.CLOSED);
  }

  private handleWebsocketMessage = (msg: IncomingMessage): void => {
    const { t } = msg;
    switch (t) {
      case IncomingMessageType.RESIZE: {
        const { width: w } = msg as ResizeMessage;
        this.resetMap(true);

        // Update participants radius when ratio changes
        this.miniMapHelper.ratio = this.getRatio({ w });
        this.radius = this.miniMapHelper.getScale(PLAYER_RADIUS);
        this.participants.forEach(this.updatePlayerRadius);
        this.sceneService.participantsAndPlayerList.forEach((player) => this.updatePlayerPosition(player));
        this.changeDetectorRef.markForCheck();
        return;
      }
      case IncomingMessageType.MOVED: {
        const { p } = msg as MovedMessage;
        this.movements = p;

        // Update positions for all moved participants
        p.forEach(({ u: userId, x, y }) => {
          this.updatePlayerPosition({ userId, x, y });
        });

        this.changeDetectorRef.markForCheck();
        return;
      }
    }
  };

  private createMapContainer(): void {
    this.mapGraphic = new Graphics()
      .beginFill(this.miniMapHelper.backgroundColor, 0.3)
      .drawRoundedRect(0, 0, this.miniMapHelper.width, this.miniMapHelper.height, 15)
      .endFill();
    this.mapGraphic.name = 'map-graphic';
    this.mapGraphic.sortableChildren = true;
  }

  /* viewport init  */
  private initViewport(isResize?: boolean) {
    if (!isPlatformBrowser(AppComponent.PLATFORM_ID || !this.app?.renderer || !this.mapGraphic)) {
      return;
    }

    this.miniMapHelper.worldWidth = this.mapWidth;
    this.miniMapHelper.worldHeight = this.mapHeight;

    const width = this.miniMapHelper.width;
    const height = this.miniMapHelper.height;
    // Create and add viewport
    if (!this.app?.renderer.events) {
      return;
    }
    this.viewport = new Viewport({
      screenWidth: width,
      screenHeight: height,
      worldWidth: this.miniMapHelper.worldWidth,
      worldHeight: this.miniMapHelper.worldHeight,
      // events: this.app.renderer.events,
    });

    if (this.viewport) {
      this.viewport
        .drag()
        .on('drag-start', () => {
          this.draggingState = true;
          this.dragging = true;
          this.isCurrentlyDragging = true;
        })
        .on('drag-end', () => {
          this.isCurrentlyDragging = false;
        })
        .on('click', (e) => {
          // Verification to avoid conflict between dragging and click when showing teleport icon
          if (!this.draggingState) {
            this.showTeleportMenu(this.viewport!.toLocal(e.global));
            this.showTeleportTooltip();
            this.draggingState = false;
            this.changeDetectorRef.markForCheck();
          } else {
            this.draggingState = false;
          }
        });

      this.viewport.sortableChildren = true;
    }

    if (!isResize) {
      this.initializeMapCenter();
    }

    // Set interaction state
    if (this.miniMapHelper.state === MiniMapState.EXPANDED && !this.miniMapHelper.isZoomOutMaxed()) {
      this.resumeDrag();
    } else {
      this.stopDrag();
    }

    // Create World Layer
    void this.createWorldLayer();

    // Reset Map Children
    if (isResize) {
      this.miniMapHelper.resetZoom();
      this.restoreArtifacts();
      this.restoreCTAItems();
      this.participants.forEach((p) => this.viewport?.addChild(p));
    }

    if (this.miniMapHelper.state === MiniMapState.EXPANDED && !this.miniMapHelper.isZoomOutMaxed()) {
      this.resumeDrag();
    }

    if (this.miniMapHelper.state === MiniMapState.EXPANDED && this.miniMapHelper.isZoomOutMaxed()) {
      this.stopDrag();
    }
  }

  private async enableMapTeleport() {
    this.teleportContainer = new Container();
    this.teleportContainer.name = 'teleportContainer';
    this.teleportContainer.eventMode = 'static';
    this.teleportContainer.zIndex = ZIndex.TeleportMenu;
    this.teleportContainer.visible = false;
    this.teleportContainer.cursor = 'pointer';

    const tooltipOffset = -35;

    this.teleportTooltipRectangleBackground = new Graphics()
      .beginFill(0x808080, 0.5)
      .drawRoundedRect(-50, -15, 100, 30, 8)
      .endFill();
    this.teleportTooltipRectangleBackground.name = 'teleportTooltipRectangleBackground';
    this.teleportTooltipRectangleBackground.x = 0;
    this.teleportTooltipRectangleBackground.y = tooltipOffset;
    this.teleportContainer.addChild(this.teleportTooltipRectangleBackground);

    this.teleportTooltip = new Text('Teleport here', {
      fontFamily: 'Arial',
      fontSize: 12,
      fill: COLOR_WHITE,
    });
    this.teleportTooltip.anchor.set(0.5, 0.5);
    this.teleportTooltip.position.set(0, tooltipOffset);
    this.teleportContainer.addChild(this.teleportTooltip);

    const circleBackground = new Graphics()
      .beginFill(0xffffff)
      .drawCircle(0, 0, 15)
      .lineStyle(1, 0x0000ff)
      // White opaque outline
      .drawCircle(0, 0, 16)
      .endFill()
      .lineStyle(4, 0xffffff, 0.3);
    this.teleportContainer.addChild(circleBackground);

    const teleportMenuTexture = await Assets.load('assets/pixi/teleport-ocean.svg');
    const teleportMenu = new Sprite(teleportMenuTexture);
    teleportMenu.name = 'teleportMenu';
    teleportMenu.anchor.set(0.5, 0.5);
    this.teleportContainer.addChild(teleportMenu);

    this.teleportContainer
      .on('click', (e) => {
        e.stopPropagation();
        if (this.teleportService.teleportEngaged()) {
          return;
        }
        const mainPlayer = this.sceneService.getPlayer();
        if (mainPlayer) {
          const worldPosition = this.getWorldCoordinates(
            Math.round(this.teleportContainer.position.x),
            Math.round(this.teleportContainer.position.y),
          );
          this.hideTeleportMenu();

          void this.teleportService.teleport(mainPlayer, worldPosition.x, worldPosition.y);
        }
      })
      .on('mouseover', () => {
        this.showTeleportTooltip();
        this.changeDetectorRef.markForCheck();
      });
    this.viewport!.addChild(this.teleportContainer);
  }

  private showTeleportTooltip(): void {
    if (this.teleportTooltipRectangleBackground && this.teleportTooltip) {
      this.teleportTooltipRectangleBackground.visible = true;
      this.teleportTooltip.visible = true;
      window.clearTimeout(this.teleportTooltipTimeout);
      this.teleportTooltipTimeout = window.setTimeout(() => this.hideTeleportTooltip(), 1000);
    }
  }

  private hideTeleportTooltip(): void {
    window.clearTimeout(this.teleportTooltipTimeout);
    if (this.teleportTooltip && this.teleportTooltipRectangleBackground) {
      this.teleportTooltip.visible = false;
      this.teleportTooltipRectangleBackground.visible = false;
    }
  }

  private showTeleportMenu(global: Point) {
    if (this.teleportContainer && this.teleportTooltipRectangleBackground) {
      this.teleportContainer.position.set(global.x, global.y);
      this.teleportContainer.visible = true;
      window.clearTimeout(this.teleportMenuTimeout);
      this.teleportMenuTimeout = window.setTimeout(() => {
        this.hideTeleportMenu();
      }, 10000);
    }
  }

  private hideTeleportMenu() {
    window.clearTimeout(this.teleportMenuTimeout);
    window.clearTimeout(this.teleportTooltipTimeout);
    if (this.teleportContainer) {
      this.teleportContainer.visible = false;
      this.hideTeleportTooltip();
    }
  }

  private restoreArtifacts(): void {
    this.artifacts.forEach((artifact) => {
      const { artifactId: rawArtifactId } = artifact;
      if (!rawArtifactId) {
        return;
      }
      const artifactId = rawArtifactId.toString();
      const sprite = this.artifactSprites.get(artifactId);
      if (!sprite) {
        return;
      }
      const { x, y } = this.getLocalCoordinates(artifact.x ?? 0, artifact.y ?? 0);
      sprite.position.x = x;
      sprite.position.y = y;
      const ratio = this.getRatio({});
      sprite.width = (artifact.width ?? 0) * ratio;
      sprite.height = (artifact.height ?? 0) * ratio;
      sprite.zIndex = ZIndex.Artifact;
      this.viewport?.addChild(sprite);
    });
  }

  private restoreCTAItems(): void {
    this.ctaItems.forEach((item) => {
      const { CTAItemId: rawId } = item;
      if (!rawId) {
        return;
      }

      const buttonId = `button:${rawId?.toString()}`;
      const imageId = `image:${rawId?.toString()}`;
      const buttonSprite = this.ctaSprites.get(buttonId);
      const imageSprite = this.ctaSprites.get(imageId);

      const ratio = this.getRatio({});
      const position = this.getLocalCoordinates(item.x ?? 0, item.y ?? 0);
      const buttonY = item.imageURI ? generateMapButtonY(position.y, item.imageHeight ?? 0, ratio) : position.y;

      if (buttonSprite) {
        buttonSprite.position.set(position.x, buttonY);
        buttonSprite.zIndex = ZIndex.CTA;
        this.viewport?.addChild(buttonSprite);
      }

      if (imageSprite) {
        imageSprite.position.set(position.x, position.y);
        imageSprite.width = (item.imageWidth ?? 0) * ratio;
        imageSprite.height = (item.imageHeight ?? 0) * ratio;
        imageSprite.zIndex = ZIndex.CTA;
        this.viewport?.addChild(imageSprite);
      }
    });
  }

  public get guestText(): string {
    return this.guestCount === 1 ? 'GUEST' : 'GUESTS';
  }

  /**
   * disables drag on viewport
   */
  private stopDrag(): void {
    this.dragging = false;
    this.draggingEnabled = false;
    this.viewport?.plugins.pause('drag');
  }

  /**
   * enables drag on viewport
   */
  private resumeDrag(): void {
    this.dragging = true;
    this.draggingEnabled = true;
    this.viewport?.plugins.resume('drag');
  }

  /**
   * hides or displays map
   */
  public toggleMap(forceState: MiniMapState | null = null): void {
    this.toggleSearchRoom();
    this.toggleReactions();
    if (forceState !== null) {
      this.setMapState(forceState);
      return;
    }
    if (this.stage?.visible) {
      // reset the participant if the map were opened by the find feature
      if (this.openedByFindFeature) {
        this.miniMapHelper.setZoom(DEFAULT_ZOOM);
        this.setTeleportScale();
        this.centerOnMap(DEFAULT_ZOOM);
        this.resetFindParticipant();
        this.store.dispatch(socialAppActions.TOGGLE_MAP_OPEN({ open: false, userId: '' }));
      }
      this.setMapState(MiniMapState.CLOSED);
    } else if (this.isMobile) {
      this.setMapState(MiniMapState.MOBILE);
    } else {
      // Try to restore the last open state, otherwise use the default mode
      const lastState = this.getLastOpenState();
      let targetState: MiniMapState;

      if (lastState) {
        // Use the last saved state, but adjust for mobile if needed
        if (this.isMobileResponsiveWidth && lastState !== MiniMapState.MOBILE) {
          targetState = MiniMapState.MOBILE;
        } else if (!this.isMobileResponsiveWidth && lastState === MiniMapState.MOBILE) {
          targetState = MiniMapState.OPENED;
        } else {
          targetState = lastState;
        }
      } else {
        // Fall back to default behavior
        if (this.isMobileResponsiveWidth) {
          targetState = MiniMapState.MOBILE;
        } else {
          targetState = MiniMapState.OPENED;
        }
      }

      this.setMapState(targetState);
    }
  }

  /**
   * determines new map state based on previous state
   */
  public resizeMap(): void {
    const newState = this.miniMapHelper.state === MiniMapState.OPENED ? MiniMapState.EXPANDED : MiniMapState.OPENED;
    this.setMapState(newState);
  }

  /**
   * sets new map state and performs resize and visibility based on new state
   */
  private setMapState(state: MiniMapState, resize = true): void {
    // Save the current state before changing to closed
    if (state === MiniMapState.CLOSED && this.miniMapHelper.state !== MiniMapState.CLOSED) {
      this.saveLastOpenState();
    }

    this.miniMapHelper.worldWidth = this.mapWidth;
    this.miniMapHelper.worldHeight = this.mapHeight;
    this.miniMapHelper.setState(state);

    if (this.stage && this.app?.renderer) {
      if (state === MiniMapState.CLOSED) {
        // Make sure that hover state is reset after closing mini map
        this.hoverUser = undefined;
        this.hoverPlayer = undefined;
        this.isMouseOver.set(false);

        this.stage.visible = false;
        (this.app.renderer.view as HTMLCanvasElement).style.display = 'none';
      } else {
        this.stage.visible = true;
        (this.app.renderer.view as HTMLCanvasElement).style.display = 'block';
      }
    }
    if (resize) {
      this.resize();
    }
  }

  public isRoomLargerThanMini(): boolean {
    return this.roomSize !== RoomSizeType.MINI;
  }

  public get hoverUserName(): string {
    return this.hoverUserNameFromBadge
      ? this.hoverUserNameFromBadge
      : this.hoverUser
        ? formatUserName(this.hoverUser)
        : '';
  }

  public get hoverPlayerColorDescription(): string {
    return this.hoverPlayer ? this.formatColorDescription(this.hoverPlayerColor as string) : '';
  }

  public get hoverPlayerColor(): string | undefined {
    return this.hoverPlayer ? this.hoverPlayer?.ringColor : '';
  }

  private formatColorDescription(color: string): string {
    const ringColor = this.ringColorsList.find((item) => {
      return item.color === color;
    });
    return ringColor ? ringColor.description : '';
  }

  /**
   * sets zoom and moves viewport accordingly
   */
  public zoom(zoomIn: boolean): void {
    // Zoom Out Maxed and zoomOut clicked
    if (this.miniMapHelper.isZoomOutMaxed() && !zoomIn) {
      return;
    }
    // Zoom In Maxed and zoomIn clicked
    if (this.miniMapHelper.isZoomInMaxed() && zoomIn) {
      return;
    }
    const zoom = this.miniMapHelper.getZoom(zoomIn);

    // Get current viewport center instead of player position
    const center = this.viewport?.center
      ? new Point(this.viewport.center.x, this.viewport.center.y)
      : this.getMainParticipantCenter();

    const options: ISnapZoomOptions = {
      width: zoom,
      time: ZOOM_SNAP_TIME,
      center: center,
    };
    this.viewport?.snapZoom(options);

    this.setTeleportScale();

    // Handle state of drag and zoom
    this.handleMapInteractionState();
  }

  // adjust scale of teleportContainer
  private setTeleportScale(): void {
    if (this.teleportContainer && this.teleportContainer && this.viewport) {
      this.teleportContainer.scale.set(1 / this.viewport.scale.x, 1 / this.viewport.scale.y);
    }
  }

  // map drag listener swallows mouse up event
  // call MouseMovementController to cancel movement
  public mouseUpEvent(): void {
    if (this.sceneService.sceneManager) {
      MouseMovementController.getInstance(this.sceneService.sceneManager).onMouseLeave();
    }
  }

  public mouseOverEvent(): void {
    if (!this.isMouseOver()) {
      this.isMouseOver.set(true);
    }
  }

  /**
   * initializes map center based on map state and room size
   */
  private initializeMapCenter(): void {
    const center = this.getMainParticipantCenter();
    this.dragging = false;
    this.viewport?.moveCenter(center).drag().pinch().decelerate();

    // Mini Map State === EXPANDED
    if (this.miniMapHelper.state === MiniMapState.EXPANDED) {
      if (this.roomSize === RoomSizeType.MINI || this.roomSize === RoomSizeType.SMALL) {
        this.centerOnMap(DEFAULT_ZOOM);
      }

      if (!this.miniMapHelper.isZoomOutMaxed()) {
        this.resumeDrag();
      }

      if (this.miniMapHelper.isZoomOutMaxed()) {
        this.stopDrag();
      }
    } else {
      if (this.roomSize === RoomSizeType.MINI) {
        const point = this.mapScale / 2;
        const mapCenter = new Point(point, point);
        const options = {
          width: DEFAULT_ZOOM,
          time: ZOOM_SNAP_TIME,
          center: mapCenter,
        };
        this.viewport?.snapZoom(options);
      }
    }
  }

  /**
   * centers player in the view port
   */
  public centerOnPlayer(): void {
    const center = this.getMainParticipantCenter();
    this.viewport?.moveCenter(center);
    this.dragging = false;
  }

  /**
   * centers map in the view port
   */
  private centerOnMap(zoom?: number): void {
    if (!this.viewport) {
      return;
    }
    const width = zoom ?? this.mapScale;
    const point = this.mapScale / 2;
    const center = new Point(point, point);
    this.viewport.moveCenter(center).drag().pinch().decelerate();

    const options = {
      width,
      time: ZOOM_SNAP_TIME,
      center,
    };
    this.viewport?.snapZoom(options);

    if (this.miniMapHelper.state === MiniMapState.EXPANDED && !this.miniMapHelper.isZoomOutMaxed()) {
      this.resumeDrag();
    }

    if (this.miniMapHelper.state === MiniMapState.EXPANDED && this.miniMapHelper.isZoomOutMaxed()) {
      this.stopDrag();
    }
  }

  private getMainParticipantCenter(): Point {
    const mainParticipant = this.sceneService.getPlayer();
    const center = this.getLocalCoordinates(mainParticipant?.x ?? 0, mainParticipant?.y ?? 0);
    if (!mainParticipant) {
      return new Point(0, 0);
    }
    return center;
  }

  /**
   * determines the ratio based on the big world size
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public getRatio({ bound = 0.1, w }: { bound?: number; w?: number }): number {
    const { width: worldWidth } = this.sceneService.worldDimensions;
    if (!worldWidth) {
      this.warn('getRatio attempted without worldWidth');
      return bound;
    }

    // const width = w ?? worldWidth * 4;
    this.miniMapHelper.worldWidth = this.mapWidth;

    // return this.miniMapHelper.worldWidth / width >= 0.1 ? this.miniMapHelper.worldWidth / width : bound;
    return bound;
  }

  private handleMapInteractionState(): void {
    this.handleInitialClosedState();

    if (this.miniMapHelper.state === MiniMapState.OPENED) {
      this.stopDrag();
      if (this.roomSize === RoomSizeType.MINI) {
        this.centerOnMap(DEFAULT_ZOOM);
        return;
      }
      this.centerOnPlayer();
    }

    if (this.miniMapHelper.state === MiniMapState.MOBILE) {
      this.resumeDrag();
    }

    if (this.miniMapHelper.state === MiniMapState.EXPANDED && !this.miniMapHelper.isZoomOutMaxed()) {
      this.resumeDrag();
    }

    if (this.miniMapHelper.state === MiniMapState.EXPANDED && this.miniMapHelper.isZoomOutMaxed()) {
      this.stopDrag();
      if (this.roomSize === RoomSizeType.MINI) {
        this.centerOnMap(DEFAULT_ZOOM);
        return;
      }
      this.centerOnMap();
    }
  }

  private handleInitialClosedState(): void {
    const state = this.setGuestMiniMap ? this.guestMiniMapState : this.defaultMode();
    if (!this.checkIsMapInitiallyClosed && state === MiniMapState.CLOSED) {
      this.stage?.removeChildren();
      this.viewport?.removeChildren();
      this.createMapContainer();

      void this.initViewport(true);
      this.initializeMapCenter();
      this.checkIsMapInitiallyClosed = true;
    }
  }

  /**
   * resizes container and map graphic and moves viewport accordingly
   */
  public resize(): void {
    if (!this.element || !this.mapGraphic || !this.viewport || !this.miniMapHelper) {
      return this.warn('resize attempted with missing info', {
        mapGraphic: this.mapGraphic,
        viewport: this.viewport,
        element: this.element,
      });
    }
    const { height, width } = this.miniMapHelper;
    this.element.style.width = this.isMobileResponsiveWidth
      ? `${width - PORTRAIT_MAP_HORIZONTAL_PADDING}px`
      : width + 'px';
    this.element.style.height = this.isMobileResponsiveWidth
      ? `${height - PORTRAIT_MAP_VERTICAL_PADDING}px`
      : height + 'px';

    if (this.app?.renderer) {
      this.app?.renderer?.resize(width, height);
    }

    this.mapGraphic.width = width;
    this.mapGraphic.height = height;
    this.viewport.screenWidth = width;
    this.viewport.screenHeight = height;
    this.stage?.scale.set(1, 1);

    this.stageHeight = height + 'px';
    this.stageWidth = width + 'px';

    // Handle Drag State and Centering
    this.handleMapInteractionState();
  }

  /**
   * get x and y in relation to the big world\
   */
  private getLocalCoordinates(x: number, y: number): Point {
    const sm = this.sceneService.sceneManager;
    this.miniMapHelper.worldHeight = this.mapHeight;
    this.miniMapHelper.worldWidth = this.mapWidth;

    if (!sm || !sm.viewport) {
      this.warn('attempted to getPosition without sceneManager');
      return new Point(x, y);
    }

    const worldWidth = sm.viewport.worldHeight ?? 0;
    const worldHeight = sm.viewport.worldWidth ?? 0;

    const mapWidth = worldWidth / this.miniMapHelper.worldWidth;
    const mapHeight = worldHeight / this.miniMapHelper.worldHeight;

    const posX = x + worldWidth / 2;
    const posY = y + worldHeight / 2;

    const newX = Math.floor(posX / mapWidth);
    const newY = Math.floor(posY / mapHeight);
    return new Point(newX, newY);
  }

  private getWorldCoordinates(x: number, y: number): Point {
    const sm = this.sceneService.sceneManager;
    this.miniMapHelper.worldHeight = this.mapHeight;
    this.miniMapHelper.worldWidth = this.mapWidth;

    if (!sm || !sm.viewport) {
      this.warn('attempted to getWorldCoordinates without sceneManager');
      return new Point(x, y);
    }

    const worldWidth = sm.viewport.worldHeight ?? 0;
    const worldHeight = sm.viewport.worldWidth ?? 0;

    const mapWidth = worldWidth / this.miniMapHelper.worldWidth;
    const mapHeight = worldHeight / this.miniMapHelper.worldHeight;

    // Reverse the calculation
    const posX = x * mapWidth;
    const posY = y * mapHeight;

    const newX = posX - worldWidth / 2;
    const newY = posY - worldHeight / 2;

    return new Point(newX, newY);
  }

  private removeArtifactSprite(sprite: Sprite | undefined, artifactId: string): void {
    if (!sprite) {
      this.warn('removeArtifactSprite attempted without sprite', { artifactId });
      return;
    }
    this.artifactSprites.delete(artifactId);
    this.viewport?.removeChild(sprite);
    sprite.destroy();
  }

  private removeCTASprite(sprite: Sprite | undefined, id: string): void {
    if (!sprite) {
      this.warn('removeCTASprite attempted without sprite', { id });
      return;
    }
    this.ctaSprites.delete(id);
    this.viewport?.removeChild(sprite);
    sprite.destroy();
  }

  // private removeNavItemSprite(sprite: Sprite | undefined, id: string): void {
  //   if (!sprite || !sprite.texture) {
  //     this.warn('removeNavSprite attempted without sprite', { id });
  //     return;
  //   }

  //   this.navItemSprites.delete(id);

  //   this.viewport?.removeChild(sprite);
  //   sprite.destroy();
  // }

  // private deleteNavItems(): void {
  //   this.navItems.forEach(({ navigationItemId: rawItemId }) => {
  //     if (!rawItemId) {
  //       return;
  //     }
  //     const itemId = rawItemId?.toString();
  //     const sprite = this.navItemSprites.get(itemId);
  //     this.removeNavItemSprite(sprite, itemId);
  //   });
  //   this.navItems = [];
  // }

  /**
   * Handles the change in the artifacts state, creates the new artifacts and
   * removes the artifacts that are no longer needed.
   *
   * @param prevArtifacts The previous state of artifacts
   * @param artifacts The new artifacts
   */
  private async handleArtifactsChange(prevArtifacts: SocialArtifact[], artifacts: SocialArtifact[]): Promise<void> {
    const newArtifacts = onlyInLeft(artifacts, prevArtifacts, (a, b) => a.artifactId === b.artifactId);
    const oldArtifacts = onlyInLeft(prevArtifacts, artifacts, (a, b) => a.artifactId === b.artifactId);

    this.artifacts = artifacts;

    const { viewport } = this;

    if (!viewport) {
      this.error('handleArtifactsChange attempted without viewport');
      return;
    }

    this.info('handleArtifactsChange', { newArtifacts, oldArtifacts });

    // Remove old artifacts
    oldArtifacts.forEach(({ artifactId: rawArtifactId }) => {
      if (!rawArtifactId) {
        return;
      }
      const artifactId = rawArtifactId?.toString();
      const sprite = this.artifactSprites.get(artifactId);
      this.removeArtifactSprite(sprite, artifactId);
    });

    // Create new artifacts
    for (const artifact of newArtifacts) {
      artifact.imageURI = this.compressed(artifact.imageURI as string);
      const sprite = await createArtifactSprite(artifact, this.getRatio({ bound: 0.05 }), this.ktx2Compression);
      if (!sprite) {
        this.error('Failed to create artifact sprite', { artifact });
        continue;
      }
      const position = this.getLocalCoordinates(sprite.position.x, sprite.position.y);
      sprite.position.set(position.x, position.y);
      sprite.zIndex = ZIndex.Artifact;
      viewport.addChild(sprite);
      if (sprite.name !== null) {
        this.artifactSprites.set(sprite.name, sprite);
      }
    }
  }

  /**
   * Handles the change in the cta items state, creates the new items and
   * removes the items that are no longer needed.
   *
   * @param prevItems The previous state of items
   * @param items The new items
   */

  private async handleCTAItemsChange(prevItems: CTAItem[], items: CTAItem[]): Promise<void> {
    const newItems = onlyInLeft(items, prevItems, (a, b) => a.CTAItemId === b.CTAItemId);
    const oldItems = onlyInLeft(prevItems, items, (a, b) => a.CTAItemId === b.CTAItemId);

    this.ctaItems = items;

    const { viewport } = this;

    if (!viewport) {
      this.error('handleCTAItemChange attempted without viewport');
      return;
    }

    this.info('handleCTAItemChange', { newItems, oldItems });

    // Remove old cta items
    // TODO: this is throwing an error
    // const oldImageURIs = oldItems.map((item) => this.compressed(item.imageURI as string)).filter((uri) => uri);
    // await Assets.unload(oldImageURIs);
    // oldItems.forEach(({ CTAItemId: rawId }) => {
    //   if (!rawId) {
    //     return;
    //   }
    //   const buttonId = `button:${rawId?.toString()}`;
    //   const imageId = `image:${rawId?.toString()}`;
    //   const buttonSprite = this.ctaSprites.get(buttonId);
    //   const imageSprite = this.ctaSprites.get(imageId);
    //   this.removeCTASprite(buttonSprite, buttonId);
    //   this.removeCTASprite(imageSprite, imageId);
    // });

    // Create new cta items
    for (const item of newItems) {
      const ratio = this.getRatio({});
      const compressedItem = { ...item, imageURI: this.compressed(item.imageURI as string) };

      const x = compressedItem.x ?? 0;
      const y = compressedItem.y ?? 0;

      const position = this.getLocalCoordinates(x, y);

      // Only create button sprite if CTA has button
      if (item.buttonVisible) {
        // const buttonSprite = createCTAButton(
        //   this.sceneService.sceneManager?.app?.renderer as Renderer,
        //   compressedItem,
        //   ratio,
        // );
        const buttonSprite = new Sprite();

        if (!buttonSprite) {
          this.error('Failed to create cta button sprite', { compressedItem });
          continue;
        }

        const buttonY = compressedItem.imageURI
          ? generateMapButtonY(position.y, compressedItem.imageHeight ?? 0, ratio)
          : position.y;

        buttonSprite.position.set(position.x, buttonY);
        buttonSprite.zIndex = ZIndex.CTA;
        viewport.addChild(buttonSprite);
        this.ctaSprites.set(buttonSprite.name ?? '', buttonSprite);
      }

      if (compressedItem.imageURI && compressedItem.imageHeight && compressedItem.imageWidth) {
        const imageSprite = await createCTAImage(compressedItem, ratio, this.ktx2Compression);
        if (!imageSprite) {
          this.error('Failed to create cta image sprite', { compressedItem });
          continue;
        }

        imageSprite.position.set(position.x, position.y);
        imageSprite.anchor.set(0.5, 0.5);
        imageSprite.zIndex = ZIndex.CTA;
        viewport.addChild(imageSprite);
        this.ctaSprites.set(imageSprite.name ?? '', imageSprite);
      }
    }
  }

  /**
   * Participant / Bubble Creation
   */
  private createParticipant(isMainParticipant: boolean, player: Player): MiniMapParticipant | null {
    if (!this.app) {
      return null;
    }
    // create mini map participant
    const { user, x, y } = player;
    const radius = this.miniMapHelper.getScale(PLAYER_RADIUS);
    const participant = new MiniMapParticipant(
      this.app,
      radius,
      user,
      this.theme,
      isMainParticipant,
      this.newVisualIndicators(),
      player.ringColor,
    );
    participant.name = `user-${user?.userId}`;

    participant.scale.set(1, 1);

    const position = this.getLocalCoordinates(x, y);
    participant.position.set(position.x, position.y);

    // listen to hover over
    participant
      .on('pointerover', () => {
        this.hoverUser = user;
        if (user.userId) {
          const { userName } = this.sceneService.sceneManager?.getParticipant(user.userId) || {};
          if (userName && userName.trim()) {
            this.hoverUserNameFromBadge = userName;
          } else {
            this.hoverUserNameFromBadge = undefined;
          }
        }
        this.hoverPlayer = player;
        this.changeDetectorRef.markForCheck();
      })

      // listen to no hover
      .on('pointerout', () => {
        this.hoverUser = undefined;
        this.hoverPlayer = undefined;
        this.isMouseOver.set(false);
        this.changeDetectorRef.markForCheck();
      })
      // steal the click event and discard (prevents teleport on top of player)
      .on('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
      });

    return participant;
  }

  private addParticipant(player: Player): void {
    const { isPrimaryPlayer, userId, x, y } = player;

    if (this.participants.has(userId)) {
      this.info('Participant has already been added to the viewport');
      return;
    }

    const participant = this.createParticipant(isPrimaryPlayer, player);
    if (!participant) {
      return;
    }
    const position = this.getLocalCoordinates(x, y);
    participant.position.set(position.x, position.y);
    participant.zIndex = ZIndex.Player;
    this.viewport?.addChild(participant);
    this.participants.set(userId, participant);
    this.guestCount = this.participants.size || 1;
  }

  private removeParticipant(userId: string): void {
    const participant = this.participants.get(userId);
    if (!participant) {
      return;
    }
    this.viewport?.removeChild(participant);
    participant.destroy();
    this.participants.delete(userId);
    this.guestCount = this.participants.size || 1;
  }

  private updatePlayerRadius = (p: MiniMapParticipant): void => {
    if (this.radius < 4 || p.radius === this.radius) {
      return;
    }
    p.setRadius(this.radius);
    p.scale.set(1, 1);
  };

  private updatePlayerPosition = ({ userId, x, y }: { userId: string; x: number; y: number }): void => {
    const participant = this.participants.get(userId);
    if (!participant) {
      return;
    }
    const isPrimaryPlayer = this.sceneService.sceneManager?.player?.userId === userId;
    const position = this.getLocalCoordinates(x, y);
    participant.position.set(position.x, position.y);

    if (isPrimaryPlayer && !this.miniMapHelper.isZoomOutMaxed()) {
      if (this.lastPosition.x !== 0 && this.lastPosition.y !== 0) {
        const hasMoved =
          Math.abs(this.lastPosition.x - x) > MIN_MOVEMNT_DISTANCE ||
          Math.abs(this.lastPosition.y - y) > MIN_MOVEMNT_DISTANCE;
        if (hasMoved) {
          this.centerOnPlayer();
        }
      }
      this.lastPosition = { x, y };
    }

    if (
      isPrimaryPlayer &&
      (this.miniMapHelper.state === MiniMapState.OPENED ||
        (this.miniMapHelper.state === MiniMapState.EXPANDED && !this.miniMapHelper.isZoomOutMaxed()) ||
        this.miniMapHelper.allowCenterShift()) &&
      !this.dragging
    ) {
      this.viewport?.moveCenter(position.x, position.y);
    }
  };

  public static instance: SocialMiniMapComponent;

  public toggleSearchRoom(): void {
    this.store.dispatch(socialAppActions.TOGGLE_SEARCH_PARTICIPANTS({ open: false }));
  }

  public toggleReactions(): void {
    this.store.dispatch(socialAppActions.TOGGLE_REACTIONS({ open: false }));
  }

  private handleFindParticipant(findParticipant: { open: boolean; userId: string }): void {
    if (findParticipant.open) {
      // Setting max zoom out after open map
      this.miniMapHelper.setZoom(this.miniMapHelper.maxZoomOut);
      this.setTeleportScale();
      this.centerOnMap(this.miniMapHelper.maxZoomOut);
      this.setMapState(MiniMapState.EXPANDED);
      this.openedByFindFeature = true;
    }
    this.foundParticipant = this.participants.get(findParticipant.userId);
    if (!this.foundParticipant) {
      return;
    }

    if (this.newVisualIndicators()) {
      if (this.foundParticipant.getChildByName('group-ring')) {
        return;
      }
      const groupGraphic = new Graphics().beginFill(COLOR_WHITE, 0.3).drawCircle(0, 0, 25).endFill();
      groupGraphic.name = 'group-ring';

      this.foundParticipant.addChildAt(groupGraphic, 0);
    } else {
      this.foundParticipant.color = 0x4fd1ba;
    }
  }

  // Return the participants to default theme
  private resetFindParticipant(): void {
    if (!this.foundParticipant) {
      return;
    }

    if (this.newVisualIndicators()) {
      const groupRing = this.foundParticipant.getChildByName('group-ring');
      const borderIndex = groupRing ? this.foundParticipant.getChildIndex(groupRing) : -1;
      if (borderIndex !== -1) {
        this.foundParticipant.removeChildAt(borderIndex);
      }
    } else {
      this.updateTheme(this.theme ?? {});
    }
  }

  public checkTopLeftArea(event: MouseEvent): void {
    // Ensure the stage is actually visible before checking
    const targetElement = event.target as HTMLElement;
    if (!targetElement || !this.stage?.visible) {
      // If map becomes hidden or target is invalid, reset state
      this.resetInteractionState();
      return;
    }

    // Use offsetX/Y relative to the target element (#stage)
    const x = event.offsetX;
    const y = event.offsetY;

    // Get current WIDTH of the element being hovered
    const elementWidth = targetElement.offsetWidth;
    const isInTargetArea =
      y >= -MAP_HOVER_PADDING && y < this.topDetectionThreshold && x >= -MAP_HOVER_PADDING && x < elementWidth / 3;
    if (isInTargetArea) {
      // --- Entered Corner ---
      this.isMouseInTopLeftArea.set(true);
      this.isMouseOver.set(true);
    } else {
      // --- Exited Corner ---
      if (this.isMouseInTopLeftArea()) {
        this.resetInteractionState();
      }
    }
  }

  public resetInteractionState(): void {
    if (this.isMouseInTopLeftArea()) {
      this.isMouseInTopLeftArea.set(false);
      this.isMouseOver.set(false);
    }
  }

  /**
   * Saves the last open state of the mini map to local storage.
   * This is used to restore the mini map state when the user returns to the page.
   */
  private saveLastOpenState(): void {
    if (isPlatformBrowser(this.platformId)) {
      const currentState = this.miniMapHelper.state;
      if (currentState !== undefined && currentState !== MiniMapState.CLOSED) {
        localStorage.setItem(MINIMAP_LAST_STATE_KEY, currentState.toString());
      }
    }
  }

  private getLastOpenState(): MiniMapState | null {
    if (isPlatformBrowser(this.platformId)) {
      const saved = localStorage.getItem(MINIMAP_LAST_STATE_KEY);
      if (saved) {
        const state = parseInt(saved, 10);
        // Validate that the state is valid
        if (state in MiniMapState && state !== MiniMapState.CLOSED) {
          return state as MiniMapState;
        }
      }
    }
    return null;
  }
}
